// CHG-1642 / ITER-3752 — Wave 1 Tier 1 — v2 dashboard Stripe Accounts
// admin port. Typed fetchers for /api/v1/stripe-accounts/* (existing
// admin endpoints, schemas in src/api/app/schemas/payment.py).
//
// Same pattern as donors.ts / campaigns.ts — convert the FastAPI paged
// envelope ({data, meta:{page,per_page,total,pages}}) into the v2
// ListPage's {items, total} shape inside the fetcher so the page
// component stays primitive-aligned.

import { apiClient } from "./client";

// ── Types ────────────────────────────────────────────────────────────

export type StripeAccountStatus =
  | "pending" | "active" | "restricted" | "rejected" | "disabled";
export type PayoutSchedule = "manual" | "daily" | "weekly" | "monthly";

export interface StripeAccount {
  id: string;
  organization_id: string;
  organization_name: string | null;
  stripe_account_id: string;
  account_status: StripeAccountStatus;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  statement_descriptor: string | null;
  support_email: string;
  payout_schedule: PayoutSchedule;
  created_at: string | null;
  updated_at: string;
}

export interface StripeAccountStats {
  total: number;
  active: number;
  pending: number;
  restricted: number;
}

export interface StripeAccountUpdate {
  account_status?: StripeAccountStatus;
  charges_enabled?: boolean;
  payouts_enabled?: boolean;
  statement_descriptor?: string | null;
  support_email?: string;
  payout_schedule?: PayoutSchedule;
}

export interface StripeAccountCapabilities {
  stripe_account_id: string;
  capabilities: Record<string, string>;
  fetched_at: string;
}

interface BackendPagedEnvelope {
  data: StripeAccount[];
  meta: { page: number; per_page: number; total: number; pages: number };
}

// ── Fetchers ─────────────────────────────────────────────────────────

export interface ListStripeAccountsParams {
  page?: number;
  pageSize?: number;
  // sortField / sortDirection / search are accepted but the backend
  // doesn't filter on them today (the admin list is small per-org).
  // Keeping the parameter shape compatible with ListPageConfig so the
  // page consumer doesn't need a shape adapter.
  sortField?: string;
  sortDirection?: "asc" | "desc";
  search?: string;
}

export async function listStripeAccounts(
  params: ListStripeAccountsParams = {},
): Promise<{ items: StripeAccount[]; total: number }> {
  // Page semantics: v2 ListPage emits zero-indexed `page`; the FastAPI
  // backend expects one-indexed `page` (per StripeAccountPagedEnvelope.meta
  // contract). Shift by 1.
  const r = await apiClient().get<BackendPagedEnvelope>("/stripe-accounts", {
    params: {
      page: (params.page ?? 0) + 1,
      per_page: params.pageSize ?? 25,
    },
  });
  return { items: r.data.data, total: r.data.meta.total };
}

export async function getStripeAccount(id: string): Promise<StripeAccount> {
  const r = await apiClient().get<StripeAccount>(`/stripe-accounts/${id}`);
  return r.data;
}

export async function updateStripeAccount(
  id: string, body: StripeAccountUpdate,
): Promise<StripeAccount> {
  const r = await apiClient().patch<StripeAccount>(
    `/stripe-accounts/${id}`, body,
  );
  return r.data;
}

export async function getStripeAccountStats(): Promise<StripeAccountStats> {
  const r = await apiClient().get<StripeAccountStats>("/stripe-accounts/stats");
  return r.data;
}

export async function getStripeAccountCapabilities(
  id: string,
): Promise<StripeAccountCapabilities> {
  const r = await apiClient().get<StripeAccountCapabilities>(
    `/stripe-accounts/${id}/capabilities`,
  );
  return r.data;
}
